/**
 * Converts a color name to its corresponding hex value
 * @param {string} colorName - The name of the color (e.g., "Green", "Black")
 * @returns {string} - The hex color value
 */
export const getColorFromName = (colorName) => {
  if (!colorName) return '#f5f5f5'; // Default light gray if no color name provided
  
  // Convert to lowercase for case-insensitive matching
  const color = colorName.toLowerCase().trim();
  
  // Basic color map with more muted/subtle colors
  const colorMap = {
    // Basic colors - more muted versions
    'black': '#404040', // Softer black
    'white': '#F8F8F8', // Slightly off-white
    'red': '#B25D69', // Muted red/rose
    'green': '#6B8E76', // Muted sage green
    'blue': '#5D7B9B', // Muted dusty blue
    'yellow': '#E3D18A', // Muted mustard
    'purple': '#8A7090', // Muted lavender
    'orange': '#D4946B', // Muted terracotta
    'pink': '#D4B0BC', // Muted dusty pink
    'brown': '#8E7562', // Muted taupe
    'grey': '#A0A0A0', // Muted gray
    'gray': '#A0A0A0', // Muted gray
    
    // Variations
    'light blue': '#B0C4DE', // Muted light blue
    'dark blue': '#36516E', // Muted navy
    'light green': '#A9C5A0', // Muted light green
    'dark green': '#45624B', // Muted forest green
    'light red': '#C99A9D', // Muted light red
    'dark red': '#804D53', // Muted burgundy
    'light yellow': '#EAE6CA', // Muted cream
    'dark yellow': '#C8B178', // Muted dark gold
    'light purple': '#C6B5C9', // Muted light lavender
    'dark purple': '#60476A', // Muted eggplant
    'light orange': '#E6C9B2', // Muted peach
    'dark orange': '#AD7853', // Muted amber
    'light pink': '#E5D1D7', // Muted blush
    'dark pink': '#AA6F7F', // Muted rose
    'light brown': '#BDA890', // Muted light taupe
    'dark brown': '#6D5744', // Muted coffee
    'light grey': '#DADADA', // Muted light gray
    'light gray': '#DADADA', // Muted light gray
    'dark grey': '#767676', // Muted charcoal
    'dark gray': '#767676', // Muted charcoal
    
    // Common specific colors
    'beige': '#E1D7C6', // Soft beige
    'tan': '#B8A89A', // Muted tan
    'navy': '#394962', // Muted navy
    'teal': '#5B8C85', // Muted teal
    'maroon': '#74464A', // Muted maroon
    'olive': '#727B5A', // Muted olive
    'khaki': '#B9B091', // Muted khaki
    'coral': '#C7837F', // Muted coral
    'turquoise': '#7BAEB8', // Muted turquoise
    'gold': '#D4B870', // Muted gold
    'silver': '#C0C0C0', // Muted silver
    'indigo': '#5D5980', // Muted indigo
    'violet': '#9A8BA5', // Muted violet
    'cyan': '#7BAEB8', // Muted cyan
    'magenta': '#AC7999', // Muted magenta
    'lime': '#A0B57D', // Muted lime
    'crimson': '#A15359', // Muted crimson
    'salmon': '#D4A298', // Muted salmon
    'plum': '#8D6E87', // Muted plum
    'chocolate': '#8D6348', // Muted chocolate
  };
  
  // Check for exact match
  if (colorMap[color]) {
    return colorMap[color];
  }
  
  // Check for partial matches (e.g., "dark blue" would match "blue")
  for (const [key, value] of Object.entries(colorMap)) {
    if (color.includes(key)) {
      return value;
    }
  }
  
  // If no match found, return a default color
  return '#e0e0e0'; // Light muted gray as fallback
};

/**
 * Determines if a color is light or dark
 * @param {string} hexColor - The hex color value
 * @returns {boolean} - True if the color is light, false if it's dark
 */
export const isLightColor = (hexColor) => {
  // Default to light if no color provided
  if (!hexColor) return true;
  
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Convert hex to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate brightness (YIQ formula)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  
  // Return true if the color is light (brightness > 128)
  return brightness > 128;
};